import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  LightBulbIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/20/solid';
import DisclaimerFooter from '../DisclaimerFooter';

const AlternativeDataPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const altDataSignals = [
    {
      id: 'transcripts-signal',
      name: 'Transcripts Signal',
      description: 'Drawing insights from earnings call transcripts using custom NLP trained on over 200,000 calls since 1999.',
      icon: ChatBubbleLeftRightIcon,
      details: `The Transcripts Signal draws on the insights hidden in earnings call transcripts. Using a custom natural language processing (NLP) engine trained on more than 200,000 calls since 1999, it evaluates tone, wording, and patterns in management commentary to forecast future stock performance. The approach blends traditional bag-of-words analysis with modern word embeddings, which are then fed into a machine learning model.

      Performance Results:
      • Backtests show that portfolios built on this signal—going long on companies with the strongest transcript scores and shorting those with the weakest—deliver strong returns
      • After controlling for sector and market factor biases, the strategy produced an average annual return of about 13.7% with a Sharpe ratio of 2.57 from 2006 through 2024
      • Performance has been especially consistent in recent years
      • Results scale smoothly across percentile buckets, reinforcing the robustness of the signal

      Our NLP engine analyzes:
      • Management tone and confidence levels
      • Word choice patterns and linguistic indicators
      • Forward-looking statements and guidance quality
      • Historical context compared to previous calls`
    },
    {
      id: 'innovation-signal',
      name: 'Innovation Signal',
      description: 'Measuring innovation intensity using proprietary patent and foreign worker visa data to quantify business innovation.',
      icon: CpuChipIcon,
      details: `The Innovation Signal measures the intensity of innovation at both the company and industry level. It uses proprietary data on patents and foreign worker visas to quantify how innovative a business really is. Importantly, recent results show that company-level innovation is becoming a stronger driver of returns than industry-level patterns, with visa data often proving even more predictive than patent counts.

      Historical Performance:
      • In backtests since 2003, the most innovative companies outperformed the least innovative ones by nearly 6% annually
      • From 2017 to mid-2024, the spread grew to about 13.6% annually with a Sharpe ratio above 1.5
      • Even simple long-only approaches, such as buying the top decile of companies, generated 15.5% annualized returns
      • Low turnover—about 2% daily per decile—means returns hold up even after accounting for transaction costs

      Key Innovation Metrics:
      • Patent quality and impact analysis
      • H1B visa data tracking specialized talent acquisition
      • Company-level vs. industry-level innovation intensity
      • R&D efficiency and intellectual property development`
    },
    {
      id: 'earnings-model',
      name: 'Earnings (Earnings & Revenue Surprise Forecasts)',
      description: 'Predicting earnings and revenue surprises with up to 80% accuracy across global markets using advanced analytical layers.',
      icon: LightBulbIcon,
      details: `Our Earnings model focuses on predicting whether companies will beat or miss Wall Street expectations for earnings and revenues. Unlike traditional consensus-based forecasts, which can be slow to adapt, our approach adds layers of insight by analyzing detailed analyst estimates, signs of earnings management, and historical time-series trends. This combination produces forecasts that are more accurate than consensus, correctly predicting surprises up to 80% of the time across global markets.

      Performance Track Record:
      • Stocks with high positive earnings scores (likely to beat earnings) have significantly outperformed those with large negative scores (likely to miss)
      • Across regions, the return spread has been between 13% and 23% annually since 2002
      • Sharpe ratios between 1.4 and 2.1 across different markets
      • Signals anticipate not just the earnings event but also stock performance in the following period

      Advanced Analytics Include:
      • Detailed analyst estimate analysis beyond simple consensus
      • Earnings management detection and quality assessment
      • Historical time-series trend analysis
      • Cross-regional validation and consistency checks
      • Forward-looking performance prediction capabilities`
    }
  ];

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center text-sm text-text-300">
            <Link to="/learn" className="hover:text-primary-100 transition-colors">
              Learn
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span className="text-text-100">Alternative Data Signals</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-100 mb-4 flex items-center gap-3">
            <LightBulbIcon className="h-8 w-8 text-primary-100" />
            Alternative Data Signals
          </h1>
          <p className="text-lg text-text-200">
            Proprietary signal sources that go beyond traditional financial metrics.
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-8 bg-bg-200 rounded-lg p-6 border border-bg-300">
          <h2 className="text-xl font-semibold text-text-100 mb-4">Beyond Traditional Data</h2>
          <p className="text-text-200 leading-relaxed">
            Traditional investment analysis relies heavily on historical financial data. Our alternative data signals provide forward-looking insights 
            by analyzing information that's often overlooked by conventional approaches. These unique data sources help us identify investment 
            opportunities before they're reflected in stock prices.
          </p>
        </div>

        {/* Alternative Data Signals */}
        <div className="space-y-4 mb-8">
          {altDataSignals.map((signal) => (
            <div key={signal.id} className="bg-bg-200 rounded-lg border border-bg-300">
              <button
                onClick={() => toggleSection(signal.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-bg-300 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <signal.icon className="h-5 w-5 text-primary-100" />
                  <div>
                    <h3 className="font-semibold text-text-100">{signal.name}</h3>
                    <p className="text-sm text-text-200 mt-1">{signal.description}</p>
                  </div>
                </div>
                {expandedSections[signal.id] ? (
                  <ChevronDownIcon className="h-5 w-5 text-text-200" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-text-200" />
                )}
              </button>
              {expandedSections[signal.id] && (
                <div className="px-6 pb-4">
                  <div className="text-text-200 leading-relaxed whitespace-pre-line">
                    {signal.details}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Important Note on Backtests */}
        <div className="bg-accent-100/5 rounded-lg p-6 border border-accent-100/20">
          <h3 className="text-lg font-semibold text-text-100 mb-4">Important Note on Backtests</h3>
          <p className="text-text-200 leading-relaxed">
            All results shown here are based on historical backtests run in a consistent way—there is no cherry-picking or optimization after the fact. 
            The signals are presented "as-is," meaning advisors see the same outcomes that would have been observed in real time, using the same data 
            available then. This provides a fair and transparent view of how these models perform in practice, and why they can be useful tools in 
            SharpeMetrix portfolios.
          </p>
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
};

export default AlternativeDataPage; 